// IE Hub - Alert Provider
// Manages global alerts, notifications, and toast messages

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Alert as MuiAlert } from '@ui/index';

interface AlertData {
  id: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  message: string;
  title?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  autoHide?: boolean;
  duration?: number;
  timestamp: number;
}

interface AlertContextType {
  alerts: AlertData[];
  showAlert: (alert: Partial<AlertData>) => string;
  hideAlert: (alertId: string) => void;
  clearAll: () => void;
  showSuccess: (message: string, options?: Partial<AlertData>) => string;
  showError: (message: string, options?: Partial<AlertData>) => string;
  showWarning: (message: string, options?: Partial<AlertData>) => string;
  showInfo: (message: string, options?: Partial<AlertData>) => string;
}

interface AlertProviderProps {
  children: React.ReactNode;
}

// Alert Context
const AlertContext = createContext<AlertContextType>({
  alerts: [],
  showAlert: () => '',
  hideAlert: () => {},
  clearAll: () => {},
  showSuccess: () => '',
  showError: () => '',
  showWarning: () => '',
  showInfo: () => ''
});

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

// Alert component for rendering individual alerts
interface AlertItemProps {
  alert: AlertData;
  onClose: (alertId: string) => void;
}

function AlertItem({ alert, onClose }: AlertItemProps) {
  return (
    <MuiAlert
      severity={alert.severity}
      variant={alert.variant || 'filled'}
      closable
      onClose={() => onClose(alert.id)}
      sx={{
        marginBottom: 1,
        borderRadius: 2,
        boxShadow: 3
      }}
    >
      {alert.title && (
        <Box sx={{ fontWeight: 600, marginBottom: 0.5 }}>
          {alert.title}
        </Box>
      )}
      {alert.message}
    </MuiAlert>
  );
}

function AlertProvider({ children }: AlertProviderProps) {
  const [alerts, setAlerts] = useState<AlertData[]>([]);

  // Hide specific alert
  const hideAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  }, []);

  // Show alert
  const showAlert = useCallback((alert: Partial<AlertData>): string => {
    const id = alert.id || `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newAlert: AlertData = {
      id,
      message: alert.message || '',
      severity: alert.severity || 'info',
      timestamp: Date.now(),
      autoHide: alert.autoHide !== false, // Default to true
      duration: alert.duration || getDurationBySeverity(alert.severity || 'info'),
      ...(alert.title && { title: alert.title }),
      ...(alert.variant && { variant: alert.variant })
    };
    
    setAlerts(prev => [...prev, newAlert]);
    
    // Auto-hide alert if configured
    if (newAlert.autoHide) {
      setTimeout(() => {
        hideAlert(id);
      }, newAlert.duration);
    }
    
    return id;
  }, [hideAlert]);

  // Listen for engine effects alerts
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const detail = e.detail || {};
      showAlert(detail);
    };
    window.addEventListener('iehub-alert', handler as EventListener);
    return () => window.removeEventListener('iehub-alert', handler as EventListener);
  }, [showAlert]);

  // Clear all alerts
  const clearAll = useCallback(() => {
    setAlerts([]);
  }, []);
  
  // Get default duration based on severity
  const getDurationBySeverity = (severity: string): number => {
    switch (severity) {
      case 'success':
        return 4000;
      case 'info':
        return 6000;
      case 'warning':
        return 8000;
      case 'error':
        return 10000; // Auto-hide errors after 10 seconds
      default:
        return 5000;
    }
  };
  
  // Convenience methods
  const showSuccess = useCallback((message: string, options: Partial<AlertData> = {}): string => {
    return showAlert({ ...options, message, severity: 'success' });
  }, [showAlert]);

  const showError = useCallback((message: string, options: Partial<AlertData> = {}): string => {
    // Default to autoHide: true for errors (10s), but allow override via options
    const autoHide = options.autoHide !== undefined ? options.autoHide : true;
    const duration = options.duration !== undefined ? options.duration : 10000;
    return showAlert({ ...options, message, severity: 'error', autoHide, duration });
  }, [showAlert]);

  const showWarning = useCallback((message: string, options: Partial<AlertData> = {}): string => {
    return showAlert({ ...options, message, severity: 'warning' });
  }, [showAlert]);

  const showInfo = useCallback((message: string, options: Partial<AlertData> = {}): string => {
    return showAlert({ ...options, message, severity: 'info' });
  }, [showAlert]);

  // Context value with convenience methods
  const contextValue: AlertContextType = {
    alerts,
    showAlert,
    hideAlert,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
  
  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      
      {/* Render alerts as snackbars */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 'auto' },
          right: { xs: 16, sm: 24 },
          zIndex: 9999,
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
          width: { xs: 'auto', sm: '100%' },
          pointerEvents: 'none'
        }}
      >
        {alerts.map(alert => (
          <Box
            key={alert.id}
            sx={{
              pointerEvents: 'auto',
              marginBottom: 1
            }}
          >
            <AlertItem
              alert={alert}
              onClose={hideAlert}
            />
          </Box>
        ))}
      </Box>
    </AlertContext.Provider>
  );
}

export default AlertProvider;
